<template>
  <div class="tf">
    <Breadcrumbs main="Турниры" title="Создать турнир"/>

    <div class="container-fluid tf-wrap">

      <!-- ======================= верхняя панель ======================= -->
      <div class="tf-topbar">
        <button type="button" class="tf-back" @click="goBack" v-b-tooltip.hover.bottom="'К списку турниров'">
          <i class="fa fa-arrow-left"></i>
        </button>

        <div class="tf-topbar__title">
          <div class="tf-topbar__name" :class="{ 'is-placeholder': !newTournamentName }">
            {{ newTournamentName || 'Новый турнир' }}
          </div>
          <div class="tf-topbar__meta">
            <span>Создание</span>
            <i class="tf-dot"></i>
            <span v-if="activeGameName">{{ activeGameName }}</span>
            <span v-else>дисциплина не выбрана</span>
            <i class="tf-dot"></i>
            <span :class="isReady ? 'text-success' : ''">
              {{ isReady ? 'все обязательные поля заполнены' : formIssues.length + ' ' + pluralIssues(formIssues.length) }}
            </span>
          </div>
        </div>

        <div class="tf-topbar__spacer"></div>

        <div class="tf-topbar__actions">
          <div class="tf-badge" :class="isReady ? 'tf-badge--ok' : 'tf-badge--warn'">
            <i class="fa" :class="isReady ? 'fa-check' : 'fa-exclamation'"></i> {{ progress }}%
          </div>
          <button type="button" class="tf-btn-ghost" @click="goBack">Отмена</button>
          <button type="button" class="tf-btn-primary" :class="{ 'tf-spin': isSaving }"
                  :disabled="isSaving" @click="submitForm">
            <i class="fa" :class="isSaving ? 'fa-spinner' : 'fa-check'"></i>
            Создать турнир
            <kbd class="tf-kbd">{{ saveHint }}</kbd>
          </button>
        </div>
      </div>

      <div class="tf-layout">

        <!-- ======================= навигация ======================= -->
        <aside class="tf-nav">
          <button v-for="section in sections" :key="section.key" type="button"
                  class="tf-nav__item" :class="{ 'is-on': activeSection === section.key }"
                  @click="goSection(section.key)">
            <i class="fa" :class="section.icon"></i>
            <span class="tf-nav__label">{{ section.label }}</span>
            <span v-if="issuesBySection[section.key]" class="tf-nav__badge">
              {{ issuesBySection[section.key].length }}
            </span>
            <span v-else-if="!section.optional" class="tf-nav__ok"><i class="fa fa-check"></i></span>
          </button>

          <div class="tf-nav__foot">
            <div class="tf-nav__progress-top">
              <span>Готовность</span>
              <span>{{ progress }}%</span>
            </div>
            <div class="tf-nav__track">
              <div class="tf-nav__fill" :class="{ 'is-done': isReady }" :style="{ width: progress + '%' }"></div>
            </div>
          </div>
        </aside>

        <!-- ======================= контент ======================= -->
        <div class="tf-main">
          <div ref="sectionTop"></div>

          <!-- восстановление черновика -->
          <div v-if="draftFound" class="tf-alert tf-alert--info">
            <i class="fa fa-history tf-alert__icon"></i>
            <div class="tf-alert__body">
              <div class="tf-alert__title">Найден несохранённый черновик</div>
              <div class="tf-hint">Последнее изменение: {{ draftFound.savedAt }}. Можно продолжить с того же места.</div>
              <div class="tf-alert__list">
                <button type="button" class="tf-btn-ghost" @click="restoreDraft">
                  <i class="fa fa-undo"></i> Восстановить
                </button>
                <button type="button" class="tf-btn-ghost" @click="discardDraft">
                  <i class="fa fa-trash-o"></i> Удалить черновик
                </button>
              </div>
            </div>
          </div>

          <!-- список незаполненных полей -->
          <div v-if="showProblems && formIssues.length" class="tf-alert">
            <i class="fa fa-exclamation-triangle tf-alert__icon"></i>
            <div class="tf-alert__body">
              <div class="tf-alert__title">
                Не заполнено обязательных полей: {{ formIssues.length }}
              </div>
              <div class="tf-hint">Нажмите на поле, чтобы перейти к нему.</div>
              <div class="tf-alert__list">
                <button v-for="issue in formIssues" :key="issue.label" type="button"
                        class="tf-issue" @click="goToIssue(issue)">
                  <i class="fa fa-arrow-right"></i> {{ issue.label }}
                </button>
              </div>
            </div>
            <button type="button" class="tf-alert__close" @click="showProblems = false">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <ValidationObserver ref="form" tag="div">

            <!-- ============ ОСНОВНЫЕ ============ -->
            <section v-show="activeSection === 'main'" class="tf-sec">

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-magic"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Шаблон</h3>
                    <p class="tf-card__sub">Автозаполнение всех вкладок по ранее сохранённой заготовке</p>
                  </div>
                  <router-link :to="{ name: 'templates_list' }" class="tf-link">
                    <i class="fa fa-external-link"></i> Шаблоны
                  </router-link>
                </div>
                <div class="tf-card__body">
                  <div class="tf-inline">
                    <b-form-select
                        v-model="selectedTemplateId"
                        :options="templateOptions"
                        @change="applyTemplate"
                        :disabled="isTemplateLoading"
                        style="max-width: 420px"
                    ></b-form-select>
                    <span v-if="isTemplateLoading" class="tf-badge tf-badge--primary tf-spin">
                      <i class="fa fa-spinner"></i> Загрузка
                    </span>
                    <span v-else-if="appliedTemplateName" class="tf-badge tf-badge--ok">
                      <i class="fa fa-check"></i> Применён: {{ appliedTemplateName }}
                    </span>
                  </div>
                  <div class="tf-hint">Шаблон перезаписывает заполненные поля — применяйте его до ручного ввода.</div>
                </div>
              </div>

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-sliders"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Параметры турнира</h3>
                    <p class="tf-card__sub">Основные настройки, без них турнир не создастся</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-grid">

                    <div class="tf-field tf-field--8" data-field="name">
                      <label class="tf-label tf-label--req">Название турнира</label>
                      <ValidationProvider name="Название турнира" rules="min:2|required" v-slot="{ errors }">
                        <b-form-input v-model="newTournamentName" type="text"
                                      placeholder="Например: BIGPLAY Cup #12"
                                      :state="!errors.length ? null : false"></b-form-input>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="game">
                      <label class="tf-label tf-label--req">Дисциплина</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="games" v-model="gameId"
                                       @change="handleGameChange"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-hint">От дисциплины зависят форматы и список Tier.</div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="variation">
                      <label class="tf-label tf-label--req">Тип турнира</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="variations" v-model="newVariation"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div v-if="newVariation === 4" class="tf-field tf-field--4" data-field="bpl">
                      <label class="tf-label tf-label--req">Сезон BPL</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="bplSeasons" v-model="bpl_season_id"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field" :class="newVariation === 4 ? 'tf-field--4' : 'tf-field--4'" data-field="organizer">
                      <label class="tf-label tf-label--req">Организатор</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="organizers" v-model="organizerId"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="format">
                      <label class="tf-label tf-label--req">
                        Формат
                        <span v-if="!gameId" class="tf-label__tag">нужна дисциплина</span>
                      </label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="formats" v-model="newFormats" :disabled="!gameId"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="prize">
                      <label class="tf-label tf-label--req">Призовой фонд</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-input type="text" v-model="newPrizeFond"
                                      placeholder="Например: 1000$"
                                      :state="!errors.length ? null : false"></b-form-input>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-hint">Текст, который увидят игроки на карточке турнира.</div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="slots">
                      <label class="tf-label tf-label--req">Количество команд</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-input type="number" min="2" v-model="newSlotCount"
                                      placeholder="16"
                                      :state="!errors.length ? null : false"></b-form-input>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-presets">
                        <button v-for="n in slotPresets" :key="'slot' + n" type="button"
                                class="tf-preset" @click="newSlotCount = n">{{ n }}</button>
                      </div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="country">
                      <label class="tf-label">Страна</label>
                      <b-form-select v-model="newCountry" :options="countries"></b-form-select>
                    </div>

                    <div class="tf-field tf-field--8" data-field="divisions">
                      <label class="tf-label tf-label--req">
                        Подача для Tier
                        <span v-if="!gameId" class="tf-label__tag">нужна дисциплина</span>
                      </label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <Multiselect :options="divisions" v-model="selectedDivision"
                                     label="text" track-by="value"
                                     :multiple="true" :hide-selected="true"
                                     :disabled="!gameId"
                                     placeholder="Выберите дивизионы"
                                     select-label="" selected-label="" deselect-label=""></Multiselect>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-hint">Кто может подавать заявку. «Все» выбирает весь список.</div>
                    </div>

                    <div class="tf-field tf-field--6" data-field="youtube">
                      <label class="tf-label">Ссылка трансляции</label>
                      <b-form-input type="text" v-model="newYoutubeLink"
                                    placeholder="https://youtube.com/..."></b-form-input>
                    </div>

                    <div class="tf-field tf-field--6" data-field="series">
                      <label class="tf-label">Серия турниров</label>
                      <b-form-input type="text" v-model="selectedTournamentSeries"
                                    placeholder="ID турниров через запятую: 120, 121"></b-form-input>
                      <div class="tf-hint">Связанные турниры показываются блоком на странице.</div>
                    </div>

                    <div class="tf-field" data-field="features">
                      <label class="tf-label">Features</label>
                      <Multiselect :options="featuresList" v-model="selectedFeature"
                                   label="text" track-by="value"
                                   :multiple="true" :hide-selected="true"
                                   placeholder="Дополнительные возможности"
                                   select-label="" selected-label="" deselect-label=""></Multiselect>
                    </div>

                  </div>
                </div>
              </div>

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-toggle-on"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Доступ и общение</h3>
                    <p class="tf-card__sub">Видимость турнира и чаты</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-toggles">
                    <div class="tf-toggle" :class="{ 'is-on': isHashTournament }">
                      <b-form-checkbox v-model="isHashTournament" switch>
                        <span class="tf-toggle__title">Только по ссылке</span>
                        <span class="tf-toggle__text">Турнир скрыт из публичного списка, доступ по секретной ссылке</span>
                      </b-form-checkbox>
                    </div>
                    <div class="tf-toggle" :class="{ 'is-on': withComments }">
                      <b-form-checkbox v-model="withComments" switch>
                        <span class="tf-toggle__title">Чат-флудилка</span>
                        <span class="tf-toggle__text">Общий чат для всех участников и зрителей</span>
                      </b-form-checkbox>
                    </div>
                    <div class="tf-toggle" :class="{ 'is-on': withCapChats }">
                      <b-form-checkbox v-model="withCapChats" switch>
                        <span class="tf-toggle__title">Кэп-чат</span>
                        <span class="tf-toggle__text">Отдельный чат капитанов команд и администрации</span>
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- ============ РАСПИСАНИЕ ============ -->
            <section v-show="activeSection === 'time'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-clock-o"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Даты и часовой пояс</h3>
                    <p class="tf-card__sub">Все даты указываются в выбранном часовом поясе</p>
                  </div>
                </div>
                <div class="tf-card__body">

                  <div class="tf-grid">
                    <div class="tf-field tf-field--6" data-field="timezone">
                      <label class="tf-label tf-label--req">Часовой пояс</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <b-form-select :options="timezones" v-model="newTimezone"
                                       :state="!errors.length ? null : false"></b-form-select>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>

                    <div class="tf-field tf-field--4" data-field="startReg">
                      <label class="tf-label tf-label--req">Начало регистрации</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <DatePicker
                            ref="startRegPicker"
                            mode="dateTime"
                            v-model="newStartReg"
                            is24hr
                            :model-config="modelConfig"
                            :popover="{ visibility: 'click', placement: 'bottom-start', keepVisibleOnInput: true }"
                            @popoverWillShow="handleCalendarOpen('startReg')"
                            @popoverWillHide="handleCalendarClose"
                        >
                          <template v-slot="{ inputValue, inputEvents }">
                            <b-form-input :value="inputValue" v-on="inputEvents"
                                          :state="!errors.length ? null : false"
                                          placeholder="Выберите дату и время"
                                          data-date-picker="startReg" readonly/>
                          </template>
                        </DatePicker>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-presets">
                        <button type="button" class="tf-preset" @click="newStartReg = shiftDate(null, { minutes: 5 })">Сейчас</button>
                        <button type="button" class="tf-preset" @click="newStartReg = shiftDate(null, { days: 1, atHour: 12 })">Завтра 12:00</button>
                      </div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="endReg">
                      <label class="tf-label tf-label--req">Завершение регистрации</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <DatePicker
                            ref="endRegPicker"
                            mode="dateTime"
                            v-model="newEndReg"
                            is24hr
                            :model-config="modelConfig"
                            :popover="{ visibility: 'click', placement: 'bottom-start', keepVisibleOnInput: true }"
                            @popoverWillShow="handleCalendarOpen('endReg')"
                            @popoverWillHide="handleCalendarClose"
                        >
                          <template v-slot="{ inputValue, inputEvents }">
                            <b-form-input :value="inputValue" v-on="inputEvents"
                                          :state="!errors.length ? null : false"
                                          placeholder="Выберите дату и время"
                                          data-date-picker="endReg" readonly/>
                          </template>
                        </DatePicker>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-presets">
                        <button type="button" class="tf-preset" :disabled="!newStartReg"
                                @click="newEndReg = shiftDate(newStartReg, { days: 1 })">+1 день
                        </button>
                        <button type="button" class="tf-preset" :disabled="!newStartReg"
                                @click="newEndReg = shiftDate(newStartReg, { days: 3 })">+3 дня
                        </button>
                        <button type="button" class="tf-preset" :disabled="!newStartReg"
                                @click="newEndReg = shiftDate(newStartReg, { days: 7 })">+7 дней
                        </button>
                      </div>
                    </div>

                    <div class="tf-field tf-field--4" data-field="startTournament">
                      <label class="tf-label tf-label--req">Начало турнира</label>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <DatePicker
                            ref="startTournamentPicker"
                            mode="dateTime"
                            v-model="newStartTournament"
                            is24hr
                            :model-config="modelConfig"
                            :popover="{ visibility: 'click', placement: 'bottom-start', keepVisibleOnInput: true }"
                            @popoverWillShow="handleCalendarOpen('startTournament')"
                            @popoverWillHide="handleCalendarClose"
                        >
                          <template v-slot="{ inputValue, inputEvents }">
                            <b-form-input :value="inputValue" v-on="inputEvents"
                                          :state="!errors.length ? null : false"
                                          placeholder="Выберите дату и время"
                                          data-date-picker="startTournament" readonly/>
                          </template>
                        </DatePicker>
                        <div class="tf-err">{{ errors[0] }}</div>
                      </ValidationProvider>
                      <div class="tf-presets">
                        <button type="button" class="tf-preset" :disabled="!newEndReg"
                                @click="newStartTournament = shiftDate(newEndReg, { minutes: 0 })">Сразу после рег.
                        </button>
                        <button type="button" class="tf-preset" :disabled="!newEndReg"
                                @click="newStartTournament = shiftDate(newEndReg, { hours: 1 })">+1 час
                        </button>
                        <button type="button" class="tf-preset" :disabled="!newEndReg"
                                @click="newStartTournament = shiftDate(newEndReg, { days: 1 })">+1 день
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="tf-timeline mt-4">
                    <div class="tf-timeline__step">
                      <div class="tf-timeline__cap">Старт регистрации</div>
                      <div class="tf-timeline__val" :class="{ 'is-empty': !newStartReg }">
                        {{ newStartReg ? prettyDate(newStartReg) : 'не выбрано' }}
                      </div>
                      <div v-if="newStartReg" class="tf-timeline__sub">{{ weekday(newStartReg) }}</div>
                    </div>
                    <div class="tf-timeline__step">
                      <div class="tf-timeline__cap">Конец регистрации</div>
                      <div class="tf-timeline__val" :class="{ 'is-empty': !newEndReg }">
                        {{ newEndReg ? prettyDate(newEndReg) : 'не выбрано' }}
                      </div>
                      <div v-if="newStartReg && newEndReg" class="tf-timeline__sub">
                        окно регистрации — {{ durationHuman(newStartReg, newEndReg) }}
                      </div>
                    </div>
                    <div class="tf-timeline__step">
                      <div class="tf-timeline__cap">Старт турнира</div>
                      <div class="tf-timeline__val" :class="{ 'is-empty': !newStartTournament }">
                        {{ newStartTournament ? prettyDate(newStartTournament) : 'не выбрано' }}
                      </div>
                      <div v-if="newEndReg && newStartTournament" class="tf-timeline__sub">
                        после закрытия заявок — {{ durationHuman(newEndReg, newStartTournament) }}
                      </div>
                    </div>
                  </div>

                  <div v-if="chronoMessage" class="tf-chrono" :class="{ 'is-ok': chronoOk }">
                    <i class="fa" :class="chronoOk ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                    {{ chronoMessage }}
                  </div>
                </div>
              </div>
            </section>

            <!-- ============ КОНТЕНТ ============ -->
            <section v-show="activeSection === 'content'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-langbar">
                  <span class="tf-langbar__label">Язык контента</span>
                  <button v-for="lang in languages" :key="'c-lang-' + lang.id" type="button"
                          class="tf-lang" :class="{ 'is-on': language === lang.language.toLowerCase() }"
                          @click="getLanguageFromBtn(lang.language.toLowerCase())">
                    <img :src="lang.img" :alt="lang.language">
                    {{ lang.language }}
                    <span class="tf-lang__state"
                          :class="contentFilled(lang.language.toLowerCase()) ? 'is-filled' : 'is-empty'"></span>
                  </button>
                  <span class="tf-hint ml-auto mt-0">Обязателен только русский — остальные языки по желанию</span>
                </div>

                <div class="tf-card__body">
                  <div class="tf-field mb-4" data-field="schedule">
                    <label class="tf-label" :class="{ 'tf-label--req': language === 'ru' }">
                      Расписание
                      <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                    </label>
                    <div class="tf-editor">
                      <template v-for="lang in Object.keys(newSchedule)">
                        <SunEditor :key="lang + '-schedule'" v-if="language === lang"
                                   v-model="newSchedule[lang]" :editorId="'schedule'"/>
                      </template>
                    </div>
                    <div v-if="!hasText(newSchedule.ru)" class="tf-err">
                      <i class="fa fa-exclamation-circle"></i> Расписание на русском — обязательное поле
                    </div>
                  </div>
                  <div class="tf-field mb-4" data-field="description">
                    <label class="tf-label" :class="{ 'tf-label--req': language === 'ru' }">
                      Описание
                      <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                    </label>
                    <div class="tf-editor">
                      <template v-for="lang in Object.keys(newDescription)">
                        <SunEditor :key="lang + '-description'" v-if="language === lang"
                                   v-model="newDescription[lang]" :editorId="'description'"/>
                      </template>
                    </div>
                    <div v-if="!hasText(newDescription.ru)" class="tf-err">
                      <i class="fa fa-exclamation-circle"></i> Описание на русском — обязательное поле
                    </div>
                  </div>
                  <div class="tf-field " data-field="rules">
                    <label class="tf-label" :class="{ 'tf-label--req': language === 'ru' }">
                      Правила
                      <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                    </label>
                    <div class="tf-editor">
                      <template v-for="lang in Object.keys(newRules)">
                        <SunEditor :key="lang + '-rules'" v-if="language === lang"
                                   v-model="newRules[lang]" :editorId="'rules'"/>
                      </template>
                    </div>
                    <div v-if="!hasText(newRules.ru)" class="tf-err">
                      <i class="fa fa-exclamation-circle"></i> Правила на русском — обязательное поле
                    </div>
                  </div>
                </div>
              </div>

              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-picture-o"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Обложки</h3>
                    <p class="tf-card__sub">Перетащите файл в область или выберите вручную</p>
                  </div>
                </div>
                <div class="tf-card__body">
                  <div class="tf-media">

                    <div class="tf-drop" :class="{ 'is-over': dragTarget === 'desktop' }"
                         @dragover.prevent="dragTarget = 'desktop'"
                         @dragleave.prevent="dragTarget = null"
                         @drop.prevent="onCoverDrop($event, 'desktop')">
                      <div class="tf-drop__head">
                        <i class="fa fa-desktop"></i> Десктопная обложка
                        <span class="tf-drop__pick" @click="pickFile('coverInput')">Выбрать файл</span>
                      </div>
                      <div v-if="coverPreview" class="tf-drop__preview">
                        <img :src="coverPreview" alt="Обложка">
                        <span class="tf-drop__badge">Новая</span>
                        <button type="button" class="tf-drop__remove" @click="clearCover('desktop')">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div v-else class="tf-drop__zone" @click="pickFile('coverInput')">
                        <i class="fa fa-cloud-upload fa-2x"></i>
                        <span>Перетащите изображение сюда</span>
                      </div>
                      <div v-if="coverMeta" class="tf-drop__file">{{ coverMeta.name }} · {{ coverMeta.size }}</div>
                      <input ref="coverInput" type="file" accept="image/*" @change="onCoverChange($event, 'desktop')">
                    </div>

                    <div class="tf-drop" :class="{ 'is-over': dragTarget === 'mobile' }"
                         @dragover.prevent="dragTarget = 'mobile'"
                         @dragleave.prevent="dragTarget = null"
                         @drop.prevent="onCoverDrop($event, 'mobile')">
                      <div class="tf-drop__head">
                        <i class="fa fa-mobile"></i> Мобильная обложка
                        <span class="tf-drop__pick" @click="pickFile('mobileInput')">Выбрать файл</span>
                      </div>
                      <div v-if="mobilePreview" class="tf-drop__preview">
                        <img :src="mobilePreview" alt="Мобильная обложка">
                        <span class="tf-drop__badge">Новая</span>
                        <button type="button" class="tf-drop__remove" @click="clearCover('mobile')">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                      <div v-else class="tf-drop__zone" @click="pickFile('mobileInput')">
                        <i class="fa fa-cloud-upload fa-2x"></i>
                        <span>Перетащите изображение сюда</span>
                      </div>
                      <div v-if="mobileMeta" class="tf-drop__file">{{ mobileMeta.name }} · {{ mobileMeta.size }}</div>
                      <input ref="mobileInput" type="file" accept="image/*" @change="onCoverChange($event, 'mobile')">
                    </div>

                  </div>
                </div>
              </div>
            </section>

            <!-- ============ ОЧКИ ЗА МЕСТО ============ -->
            <section v-show="activeSection === 'points'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-table"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Очки за место</h3>
                    <p class="tf-card__sub">Начисление очков по занятому месту в матче</p>
                  </div>
                  <span class="tf-badge">{{ tournamentPlaces.length }} мест</span>
                </div>
                <div class="tf-card__body">
                  <AddTournamentPlace></AddTournamentPlace>
                </div>
              </div>
            </section>

            <!-- ============ ПРИЗЫ ============ -->
            <section v-show="activeSection === 'prizes'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-card__head">
                  <span class="tf-card__icon"><i class="fa fa-trophy"></i></span>
                  <div class="tf-card__titles">
                    <h3 class="tf-card__title">Призовой фонд по местам</h3>
                    <p class="tf-card__sub">Распределение призовых между призёрами</p>
                  </div>
                  <span class="tf-badge">{{ tournamentPrizes.length }} мест</span>
                </div>
                <div class="tf-card__body">
                  <AddTournamentPrize/>
                </div>
              </div>
            </section>

            <!-- ============ SEO ============ -->
            <section v-show="activeSection === 'meta'" class="tf-sec">
              <div class="tf-card">
                <div class="tf-langbar">
                  <span class="tf-langbar__label">Язык мета-данных</span>
                  <button v-for="lang in languages" :key="'m-lang-' + lang.id" type="button"
                          class="tf-lang" :class="{ 'is-on': language === lang.language.toLowerCase() }"
                          @click="getLanguageFromBtn(lang.language.toLowerCase())">
                    <img :src="lang.img" :alt="lang.language">
                    {{ lang.language }}
                    <span class="tf-lang__state"
                          :class="metaFilled(lang.language.toLowerCase()) ? 'is-filled' : 'is-empty'"></span>
                  </button>
                </div>
                <div class="tf-card__body">
                  <div class="tf-grid">
                    <div class="tf-field" data-field="metaTitle">
                      <label class="tf-label">
                        Мета-заголовок
                        <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                      </label>
                      <b-form-input v-model="newMetaTitle[language]" placeholder="Title для поисковиков"></b-form-input>
                      <div class="tf-hint">
                        {{ (newMetaTitle[language] || '').length }} символов — оптимально до 60
                      </div>
                    </div>
                    <div class="tf-field" data-field="metaDescription">
                      <label class="tf-label">
                        Мета-описание
                        <span class="tf-label__tag">{{ language.toUpperCase() }}</span>
                      </label>
                      <b-textarea v-model="newMetaDescription[language]" rows="4"
                                  placeholder="Description для поисковиков"></b-textarea>
                      <div class="tf-hint">
                        {{ (newMetaDescription[language] || '').length }} символов — оптимально до 160
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </ValidationObserver>

          <div class="tf-savebar">
            <button type="button" class="tf-btn-ghost" @click="goBack">Отмена</button>
            <button type="button" class="tf-btn-primary" :disabled="isSaving" @click="submitForm">
              <i class="fa fa-check"></i> Создать турнир
            </button>
          </div>

          <div v-if="errorMessage" class="tf-alert mt-3">
            <i class="fa fa-exclamation-circle tf-alert__icon"></i>
            <div class="tf-alert__body">{{ errorMessage }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {DatePicker} from 'v-calendar';
import {TournamentAdapterMixin} from "../../mixins/Tournament/TournamentAdapterMixin";
import {LanguageAdapterMixin} from "../../mixins/Language/LanguageAdapterMixin";
import {TournamentFormUxMixin} from "@/mixins/Tournament/TournamentFormUxMixin";
import Multiselect from "vue-multiselect";
import {FeaturesAdapterMixin} from "@/mixins/Features/FeaturesAdapterMixin";
import {DivisionsAdapterMixin} from "@/mixins/Divisions/DivisionsAdapterMixin";
import AddTournamentPlace from "@/pages/Tournament/AddTournamentPlace.vue";
import AddTournamentPrize from './AddTournamentPrize.vue';
import SunEditor from "@/components/SunEditor.vue";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import api from "@/api/api";

const DRAFT_KEY = 'bp:tournament:create-draft:v1';

export default {
  name: 'CreateTournament',
  mixins: [TournamentAdapterMixin, LanguageAdapterMixin, FeaturesAdapterMixin, DivisionsAdapterMixin, TournamentFormUxMixin],
  components: {SunEditor, DatePicker, Multiselect, AddTournamentPlace, AddTournamentPrize, ValidationObserver, ValidationProvider},
  data() {
    return {
      modelConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD HH:mm',
      },
      activeCalendar: null,
      selectedDivision: [],
      selectedTemplateId: null,
      templateOptions: [{value: null, text: '— Без шаблона —'}],
      isTemplateLoading: false,
      appliedTemplateName: '',
      slotPresets: [8, 16, 24, 32, 64],
      draftFound: null,
      draftTimer: null,
      isBootstrapped: false,
    }
  },
  computed: {
    sections() {
      return [
        {key: 'main', label: 'Основные', icon: 'fa-sliders'},
        {key: 'time', label: 'Расписание', icon: 'fa-clock-o'},
        {key: 'content', label: 'Контент', icon: 'fa-file-text-o'},
        {key: 'points', label: 'Очки за место', icon: 'fa-table', optional: true},
        {key: 'prizes', label: 'Призовой фонд', icon: 'fa-trophy', optional: true},
        {key: 'meta', label: 'SEO / мета', icon: 'fa-search', optional: true},
      ];
    },

    /** Список незаполненных обязательных полей — питает бейджи в навигации и баннер. */
    formIssues() {
      const issues = [];
      const add = (condition, section, label, field) => {
        if (condition) issues.push({section, label, field});
      };

      add(!this.newTournamentName || String(this.newTournamentName).trim().length < 2, 'main', 'Название турнира', 'name');
      add(!this.gameId, 'main', 'Дисциплина', 'game');
      add(!this.newVariation, 'main', 'Тип турнира', 'variation');
      add(this.newVariation === 4 && !this.bpl_season_id, 'main', 'Сезон BPL', 'bpl');
      add(!this.organizerId, 'main', 'Организатор', 'organizer');
      add(!this.newFormats, 'main', 'Формат', 'format');
      add(this.newPrizeFond === '' || this.newPrizeFond === null || this.newPrizeFond === undefined, 'main', 'Призовой фонд', 'prize');
      add(!this.newSlotCount, 'main', 'Количество команд', 'slots');
      add(!this.selectedDivision || !this.selectedDivision.length, 'main', 'Подача для Tier', 'divisions');

      add(!this.newTimezone, 'time', 'Часовой пояс', 'timezone');
      add(!this.newStartReg, 'time', 'Начало регистрации', 'startReg');
      add(!this.newEndReg, 'time', 'Завершение регистрации', 'endReg');
      add(!this.newStartTournament, 'time', 'Начало турнира', 'startTournament');

      add(!this.hasText(this.newSchedule.ru), 'content', 'Расписание RU', 'schedule');
      add(!this.hasText(this.newDescription.ru), 'content', 'Описание RU', 'description');
      add(!this.hasText(this.newRules.ru), 'content', 'Правила RU', 'rules');

      return issues;
    },

    requiredCount() {
      return this.newVariation === 4 ? 16 : 15;
    },

    activeGameName() {
      const game = (this.games || []).find(item => String(item.value) === String(this.gameId));
      return game ? game.text : '';
    },

    chronoOk() {
      if (!this.newStartReg || !this.newEndReg || !this.newStartTournament) return false;
      const a = this.parseDate(this.newStartReg);
      const b = this.parseDate(this.newEndReg);
      const c = this.parseDate(this.newStartTournament);
      return a < b && b <= c;
    },

    chronoMessage() {
      const a = this.parseDate(this.newStartReg);
      const b = this.parseDate(this.newEndReg);
      const c = this.parseDate(this.newStartTournament);
      if (!a || !b || !c) return '';
      if (a >= b) return 'Регистрация заканчивается раньше, чем начинается — проверьте даты.';
      if (c < b) return 'Турнир стартует до закрытия регистрации — проверьте даты.';
      return 'Порядок дат корректный.';
    },

    /** Есть ли что терять при уходе со страницы. */
    isDirty() {
      return Boolean(
          this.newTournamentName ||
          this.gameId ||
          this.newPrizeFond ||
          this.newSlotCount ||
          this.newStartReg ||
          this.hasText(this.newDescription.ru) ||
          this.hasText(this.newRules.ru) ||
          this.hasText(this.newSchedule.ru)
      );
    },

    saveHint() {
      return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘S' : 'Ctrl+S';
    },

    /** Снимок формы для автосохранения черновика. */
    draftPayload() {
      return {
        name: this.newTournamentName,
        game_id: this.gameId,
        variation_id: this.newVariation,
        bpl_season_id: this.bpl_season_id,
        organizer_id: this.organizerId,
        prize_fond: this.newPrizeFond,
        slot_count: this.newSlotCount,
        format_id: this.newFormats,
        country_id: this.newCountry,
        youtube_link: this.newYoutubeLink,
        timezone: this.newTimezone,
        hash: this.isHashTournament,
        with_comments: this.withComments,
        with_cap_chats: this.withCapChats,
        start_reg_time: this.newStartReg,
        end_reg_time: this.newEndReg,
        start_time: this.newStartTournament,
        related: this.selectedTournamentSeries,
        divisions: (this.selectedDivision || []).map(item => item.value),
        features_ids: (this.selectedFeature || []).map(item => item.value),
        description: this.newDescription,
        schedule: this.newSchedule,
        rules: this.newRules,
        meta_title: this.newMetaTitle,
        meta_description: this.newMetaDescription,
      };
    },
  },
  watch: {
    draftPayload() {
      this.scheduleDraftSave();
    },
  },
  async mounted() {
    await Promise.allSettled([
      this.grabGames(),
      this.grabOrganizers(),
      this.indexTimeZones(),
      this.getCountriesQuery(),
      this.grabFeaturesList(),
      this.grabVariations(),
      this.grabBplSeasons(),
      this.loadTemplateOptions(),
    ])

    // Автоприменение шаблона из query-параметра (?template_id=X)
    const templateId = Number(this.$route.query.template_id)
    if (templateId) {
      this.selectedTemplateId = templateId
      await this.applyTemplate(templateId)
    } else {
      this.detectDraft()
    }

    this.setupGlobalClickHandler();
    this.$nextTick(() => {
      this.isBootstrapped = true;
    });
  },
  methods: {
    goBack() {
      this.$router.push({name: 'tournaments'});
    },

    pluralIssues(count) {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return 'поле не заполнено';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'поля не заполнены';
      return 'полей не заполнено';
    },

    contentFilled(lang) {
      return this.hasText(this.newDescription[lang]) || this.hasText(this.newRules[lang]) || this.hasText(this.newSchedule[lang]);
    },

    metaFilled(lang) {
      return Boolean((this.newMetaTitle[lang] || '').length || (this.newMetaDescription[lang] || '').length);
    },

    /** Единая точка сохранения: сперва подсказываем что не заполнено, потом отдаём в стор. */
    submitForm() {
      if (this.isSaving) return;

      if (this.formIssues.length) {
        this.showProblems = true;
        const first = this.formIssues[0];
        this.goSection(first.section);
        this.$toasted.error(`Не заполнено: ${this.formIssues.length}`, {position: 'top-right', duration: 3000});
        if (this.$refs.form && this.$refs.form.validate) this.$refs.form.validate();
        return;
      }

      this.showProblems = false;
      this.isSaving = true;
      this.suppressGuard = true;
      this.clearDraft();
      this.createTournament();
      setTimeout(() => {
        this.isSaving = false;
      }, 4000);
    },

    // ------------------------------------------------ черновик
    detectDraft() {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.data) return;
        this.draftFound = {
          savedAt: new Date(parsed.savedAt).toLocaleString('ru-RU'),
          data: parsed.data,
        };
      } catch (e) {
        localStorage.removeItem(DRAFT_KEY);
      }
    },

    scheduleDraftSave() {
      if (!this.isBootstrapped || !this.isDirty || this.suppressGuard) return;
      if (this.draftTimer) clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({
            savedAt: new Date().toISOString(),
            data: this.draftPayload,
          }));
        } catch (e) { /* квота переполнена — черновик не критичен */
        }
      }, 900);
    },

    clearDraft() {
      if (this.draftTimer) clearTimeout(this.draftTimer);
      localStorage.removeItem(DRAFT_KEY);
      this.draftFound = null;
    },

    async restoreDraft() {
      const data = this.draftFound && this.draftFound.data;
      this.draftFound = null;
      if (!data) return;
      await this.applyFormData(data);
      this.$toasted.success('Черновик восстановлен', {position: 'top-right', duration: 2500});
    },

    discardDraft() {
      this.clearDraft();
      this.$toasted.success('Черновик удалён', {position: 'top-right', duration: 2000});
    },

    // ------------------------------------------------ шаблоны
    async loadTemplateOptions() {
      try {
        const res = await api.tournamentTemplate.index({per_page: 100})
        const templates = Array.isArray(res.data?.data) ? res.data.data
            : Array.isArray(res.data) ? res.data
                : []
        this.templateOptions = [
          {value: null, text: '— Без шаблона —'},
          ...templates.map(t => ({value: t.id, text: t.name}))
        ]
      } catch (e) {
        console.error('Failed to load templates', e)
      }
    },

    async applyTemplate(templateId) {
      if (!templateId) {
        this.appliedTemplateName = '';
        return
      }
      this.isTemplateLoading = true
      try {
        const res = await api.tournamentTemplate.show(templateId)
        const responseData = res.data
        const tpl = responseData?.data?.id !== undefined ? responseData.data : responseData
        const rawData = tpl?.data
        if (!rawData) return
        const d = typeof rawData === 'string' ? JSON.parse(rawData) : rawData

        await this.applyFormData(d)
        this.appliedTemplateName = tpl?.name || ''

        const t = this.$toasted.success('Шаблон применён', {
          action: {
            text: '✕',
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            }
          }
        })
        setTimeout(() => {
          if (t) t.goAway(0)
        }, 3000)
      } catch (e) {
        const te = this.$toasted.error('Не удалось загрузить шаблон', {
          action: {
            text: '✕', onClick: (e, o) => {
              o.goAway(0)
            }
          }
        })
        setTimeout(() => {
          if (te) te.goAway(0)
        }, 3000)
        console.error(e)
      } finally {
        this.isTemplateLoading = false
      }
    },

    /**
     * Заливка формы из внешнего источника (шаблон либо локальный черновик).
     * Формат данных одинаковый, поэтому логика общая.
     */
    async applyFormData(d) {
      if (d.name) this.newTournamentName = d.name
      if (d.game_id) {
        this.gameId = d.game_id
        await this.getFormatsByGameId()
      }
      if (d.variation_id !== undefined) this.newVariation = d.variation_id
      if (d.bpl_season_id !== undefined) this.bpl_season_id = d.bpl_season_id
      if (d.organizer_id) this.organizerId = d.organizer_id
      if (d.prize_fond) this.newPrizeFond = d.prize_fond
      if (d.slot_count) this.newSlotCount = d.slot_count
      if (d.format_id) this.newFormats = d.format_id
      if (d.country_id) this.newCountry = d.country_id
      if (d.youtube_link) this.newYoutubeLink = d.youtube_link
      if (d.timezone) this.newTimezone = d.timezone
      if (d.hash !== undefined) this.isHashTournament = d.hash
      if (d.with_comments !== undefined) this.withComments = d.with_comments
      if (d.with_cap_chats !== undefined) this.withCapChats = d.with_cap_chats
      if (d.start_reg_time) this.newStartReg = d.start_reg_time
      if (d.end_reg_time) this.newEndReg = d.end_reg_time
      if (d.start_time) this.newStartTournament = d.start_time

      if (d.related) {
        this.selectedTournamentSeries = d.related
      } else if (d.related_tournament_ids && d.related_tournament_ids.length) {
        this.selectedTournamentSeries = d.related_tournament_ids.join(', ')
      }

      if (d.description) this.newDescription = {...this.newDescription, ...d.description}
      if (d.schedule) this.newSchedule = {...this.newSchedule, ...d.schedule}
      if (d.rules) this.newRules = {...this.newRules, ...d.rules}
      if (d.meta_title) this.newMetaTitle = {...this.newMetaTitle, ...d.meta_title}
      if (d.meta_description) this.newMetaDescription = {...this.newMetaDescription, ...d.meta_description}

      if (d.features_ids && this.featuresList) {
        this.selectedFeature = this.featuresList.filter(f => d.features_ids.includes(f.value))
      }

      if (d.divisions && this.divisions) {
        this.selectedDivision = this.divisions.filter(div => d.divisions.includes(div.value))
      }

      if (d.tournament_place_points) {
        try {
          const pts = typeof d.tournament_place_points === 'string'
              ? JSON.parse(d.tournament_place_points)
              : d.tournament_place_points
          this.tournamentPlaces = pts.map((item, index) => ({
            id: index + 1,
            place: item.place,
            place_pts: item.place_pts,
          }))
        } catch (e) { /* ignore */
        }
      }

      if (d.prize_fund && d.prize_fund.length) {
        this.tournamentPrizes = d.prize_fund.map((item, index) => ({
          id: index + 1,
          place: item.place,
          prize_data: item.prize_data || {amount: 0},
        }))
      }
    },

    handleGameChange() {
      this.selectedDivision = [];
      this.newFormats = null;
      this.getFormatsByGameId();
    },

    // ------------------------------------------------ календари
    setupGlobalClickHandler() {
      this.globalClickHandler = (event) => {
        const isInsideCalendar = event.target.closest('.vc-container') ||
            event.target.closest('.vc-popover-content-wrapper') ||
            event.target.closest('[data-date-picker]') ||
            event.target.closest('.vc-popover-trigger');

        if (!isInsideCalendar && this.activeCalendar) {
          setTimeout(() => {
            this.activeCalendar = null;
          }, 50);
        }
      };

      document.addEventListener('click', this.globalClickHandler, {capture: false});
    },

    handleCalendarOpen(calendarName) {
      this.closeOtherCalendars(calendarName);
      this.activeCalendar = calendarName;
    },

    handleCalendarClose() {
      this.activeCalendar = null;
    },

    closeOtherCalendars(currentCalendar) {
      const calendars = {
        startReg: 'startRegPicker',
        endReg: 'endRegPicker',
        startTournament: 'startTournamentPicker'
      };

      Object.keys(calendars).forEach(calendarKey => {
        if (calendarKey !== currentCalendar) {
          const pickerRef = calendars[calendarKey];
          if (this.$refs[pickerRef] && this.$refs[pickerRef].hidePopover) {
            try {
              this.$refs[pickerRef].hidePopover();
            } catch (error) {
              console.warn('Не удалось закрыть календарь:', pickerRef);
            }
          }
        }
      });
    },
  },

  async beforeRouteLeave(to, from, next) {
    if (this.suppressGuard || !this.isDirty) {
      next();
      return;
    }
    const ok = await this.confirmLeave();
    next(ok === true);
  },

  destroyed() {
    this.gameId = null;
  },

  beforeDestroy() {
    if (this.globalClickHandler) {
      document.removeEventListener('click', this.globalClickHandler);
    }
    if (this.draftTimer) clearTimeout(this.draftTimer);
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import "~@/assets/scss/tournament-form.scss";

/* Календарь всегда поверх карточек формы */
::v-deep .vc-container {
  position: relative;
  z-index: 1050;
}

::v-deep [data-date-picker] {
  cursor: pointer;
  user-select: none;
}
</style>
